/**
 * Collection Filters - SINGLE SOURCE OF TRUTH
 * 
 * This file controls ALL collection filtering logic:
 * - TYPE_TO_DB: Maps collection slugs to database type column values
 * - TITLE_SEARCH: Collections that also search product titles
 * - WIDTH_RANGES: Width-based collections (query width_in column)
 * - CAPACITY_RANGES: Capacity-based collections (extract from title)
 * - TYPE_GROUPS: Type synonyms (mini-fridges = compact-refrigerators)
 * - COLOR_TO_DB_PATTERNS: Color filtering
 * - BRAND_DISPLAY_NAMES: Brand name formatting
 * 
 * DO NOT duplicate these mappings anywhere else. If the API needs a mapping,
 * import it from here.
 */

import { ParsedCollectionUrl } from './collection-url-parser'

// =============================================================================
// TYPE_TO_DB: Maps collection slugs to database `type` column values
// 
// DB `type` column values (SELECT DISTINCT type FROM products WHERE status='active'):
//   beverage-cooler, bottom-freezer, chest-freezer, compact, compact-freezer,
//   counter-depth, french-door, kegerator, refrigerator, side-by-side,
//   top-freezer, upright-freezer, wine-cooler, ice-maker, water-dispenser
// =============================================================================

export const TYPE_TO_DB: Record<string, string[]> = {
  // === BASE CATEGORIES ===
  // 2026-05-29: classifier-only fine-grained types appended (additive — they
  // match 0 live rows until the poc→type backfill, then auto-populate).
  'refrigerators': ['refrigerator', 'french-door', 'side-by-side', 'top-freezer', 'bottom-freezer', 'counter-depth', 'compact', 'freezerless', 'drawer', 'single-door', '4-door-flex'],
  'freezers': ['upright-freezer', 'chest-freezer', 'compact-freezer', 'deep-freezer', 'drawer-freezer'],
  'specialty-appliances': ['wine-cooler', 'wine-fridge', 'beverage-cooler', 'kegerator', 'ice-maker', 'water-dispenser', 'water-cooler'],
  
  // === REFRIGERATOR TYPES (direct DB type matches) ===
  'french-door-refrigerators': ['french-door'],
  'side-by-side-refrigerators': ['side-by-side'],
  'top-freezer-refrigerators': ['top-freezer'],
  'bottom-freezer-refrigerators': ['bottom-freezer'],
  'counter-depth-refrigerators': ['counter-depth'],

  // FRIDGE-259 — three configuration types exist in `products.type` and in
  // FILTER_TYPES below, but had no slug mapping here, so their collections
  // rendered EMPTY while tagged inventory sat waiting:
  //   single-door  36 products → collection showed 0
  //   drawer       29 products → collection showed 0
  //   4-door-flex   8 products → collection showed 0
  //
  // `drawer-refrigerators` and `black-stainless-refrigerators` DID have
  // TITLE_SEARCH entries, but title text is the wrong key when a structured
  // type already exists. Measured: only 25 of 36 `single-door` products say
  // "single door" in the title, and **0 of 8** `4-door-flex` products contain
  // that phrase — so title matching could never find them. A TYPE_TO_DB entry
  // reads the column the classifier already populates.
  //
  // Each of these had then been auto-`gated` for looking empty: the
  // disposition system making a correct decision on wrong input.
  'single-door-refrigerators': ['single-door'],
  'drawer-refrigerators': ['drawer'],
  '4-door-flex-refrigerators': ['4-door-flex'],

  // === MINI/COMPACT FRIDGES - compact type only (NO french-door, NO side-by-side) ===
  'mini-fridges': ['compact'],
  'compact-refrigerators': ['compact'],
  'compact-mini-fridges': ['compact'],
  'dorm-refrigerators': ['compact'],
  // FRIDGE-259 — `dorm-fridges` is the slug that actually EXISTS as a
  // collection row; `dorm-refrigerators` is wired here in five places but has
  // no row at all. Every rule was written for a page that does not exist,
  // while the page that does exist matched nothing and sat gated at 0
  // products. SEMrush US: "dorm fridge" = 6,600 searches/month.
  'dorm-fridges': ['compact'],

  // NOTE: gaming-fridges and gaming-mini-fridges intentionally NOT in
  // TYPE_TO_DB — FRIDGE-619, 08/29. The original design ("same physical
  // inventory as mini-fridges, page exists for gamer search intent") never
  // actually served: both slugs were phantom-brand-hijacked and sat gated at
  // 0. Registering them (same card) would have let the nightly drain publish
  // 685- and 893-member duplicates of mini-fridges, which the FRIDGE-561
  // dedupe standard rejects. TITLE_SEARCH-only now: the gaming-MARKETED units
  // (measured 08/29: 4-5 live), so the pages publish through the normal >=5
  // floor only when real gaming inventory exists.

  // NOTE: apartment-refrigerators and apartment-size-refrigerators intentionally NOT in TYPE_TO_DB
  // They use TITLE_SEARCH only to avoid matching all 986 refrigerators.
  // Apartment fridges are narrower/smaller full-size models, not ALL refrigerators.
  
  // === RV REFRIGERATORS - tag-based matching via tags.ov.{for-rvs} ===
  'rv-refrigerators': ['for-rvs'],
  
  // === BUILT-IN ICE MAKERS - all ice-maker type products ===
  'built-in-ice-makers': ['ice-maker'],
  
  // NOTE: office-refrigerators and garage-refrigerators intentionally NOT in TYPE_TO_DB
  // They use TITLE_SEARCH only to avoid matching all 986 refrigerators.
  // See TITLE_SEARCH section for their keyword-based filters.
  
  // NOTE: humidity-control-refrigerators intentionally NOT in TYPE_TO_DB —
  // FRIDGE-619 measured 08/29 that its all-fridge-types entry OR'd in 913
  // refrigerators, making the page a near-duplicate of /collections/
  // refrigerators. It is TITLE_SEARCH-only (12 real humidity/dual-evaporator
  // units), same pattern as office/garage/apartment above.

  // === FREEZER TYPES (direct DB type matches) ===
  'upright-freezers': ['upright-freezer'],
  'chest-freezers': ['chest-freezer'],
  // deep-freezer ≈ chest-freezer (legacyTypeForClassifierType collapses them);
  // keep chest-freezer for current data + add the true type for post-backfill.
  'deep-freezers': ['chest-freezer', 'deep-freezer'],
  'drawer-freezers': ['drawer-freezer'],
  'freezerless-refrigerators': ['freezerless'],
  'compact-freezers': ['compact-freezer'],
  'mini-freezers': ['compact-freezer'],
  'small-chest-freezers': ['chest-freezer', 'compact-freezer'],
  'undercounter-freezers': ['compact-freezer', 'upright-freezer'],
  
  // === OUTDOOR FREEZERS - freezer types only (NO fridges) ===
  'outdoor-freezers': ['upright-freezer', 'chest-freezer', 'compact-freezer'],
  
  // === OFFICE/GARAGE FREEZERS - ALL freezer types ===
  'office-freezers': ['upright-freezer', 'chest-freezer', 'compact-freezer'],
  'garage-freezers': ['upright-freezer', 'chest-freezer', 'compact-freezer'],
  
  // === RV FREEZERS - tag-based matching via tags.ov.{for-rvs} ===
  'rv-freezers': ['for-rvs'],
  
  // NOTE: portable-freezers intentionally NOT in TYPE_TO_DB — FRIDGE-619
  // measured 08/29 that the compact-freezer mapping made it a 139-member
  // duplicate of compact-freezers. TITLE_SEARCH-only: the 10 real
  // 12V/car/camping units.

  // === SPECIALTY APPLIANCES (direct DB type matches) ===
  'wine-coolers': ['wine-cooler', 'wine-fridge'],
  'wine-fridges': ['wine-cooler', 'wine-fridge'],
  'beverage-coolers': ['beverage-cooler'],
  'beverage-fridges': ['beverage-cooler'],
  'beverage-centers': ['beverage-cooler'],
  'beer-fridges': ['beverage-cooler'],
  'kegerators': ['kegerator'],
  'ice-makers': ['ice-maker'],
  'water-dispensers': ['water-dispenser'],
  
  // === BACK BAR / BAR COOLERS (beverage, wine, beer - NOT full size fridges) ===
  // IMPORTANT: Excludes french-door, side-by-side, refrigerator types
  'back-bar-coolers': ['beverage-cooler', 'wine-cooler'],

  // === FILTER TYPES ===
  'water-filters': ['water-filter'],
  'refrigerator-water-filters': ['water-filter'],
  'refrigerator-filters': ['water-filter', 'air-filter'],
  'air-filters': ['air-filter'],
  'refrigerator-air-filters': ['air-filter'],
  'condenser-filters': ['condenser-filter'],
  'ice-machine-filters': ['ice-machine-filter'],
  'inline-filters': ['inline-filter'],
}

// Backwards compatibility alias
export const SLUG_TO_DB_TYPES = TYPE_TO_DB

// =============================================================================
// FILTER_TYPES: product `type` values that represent replacement filters
// (water/air/condenser/ice-machine/inline). These belong on /filters/[slug]
// and on filter-only /collections/* slugs (water-filters, etc.) — NEVER on
// appliance collections like mini-fridges. Used as a defensive scope gate.
// =============================================================================
export const FILTER_TYPES = [
  'water-filter',
  'air-filter',
  'condenser-filter',
  'ice-machine-filter',
  'inline-filter',
] as const

// =============================================================================
// Type families. The DB `type` column is unreliable — many products are
// mistyped (commercial ice makers as `kegerator`, organizer baskets as
// `chest-freezer`, etc.). These constants let us require that a collection's
// products belong to the correct family by adding `.in('type', allowed)` at
// the query layer. Render-time gate, no DB changes.
// =============================================================================
export const REFRIGERATOR_FAMILY_TYPES = [
  'refrigerator',
  'french-door',
  'side-by-side',
  'top-freezer',
  'bottom-freezer',
  'counter-depth',
  'compact',
  'built-in',
  // 2026-05-29: classifier fine-grained refrigerator types (additive — 0 live
  // rows until poc→type backfill). mini-fridge stays mapped to 'compact' (D5).
  'freezerless',
  'drawer',
  'single-door',
  '4-door-flex',
] as const

export const FREEZER_FAMILY_TYPES = [
  'freezer',
  'upright-freezer',
  'chest-freezer',
  'compact-freezer',
  // 2026-05-29: classifier fine-grained freezer types (additive).
  'deep-freezer',
  'drawer-freezer',
] as const

// Slugs that should be restricted to a specific narrow set of types beyond
// what TYPE_TO_DB encodes. Used for the most-polluted collections where the
// type column is unreliable enough that we need a strict whitelist at the
// query layer.
export const SLUG_ALLOWED_TYPES: Record<string, readonly string[]> = {
  // Compact / mini fridge slugs: the SIZE axis is now carried by
  // form_factor ∈ {mini,compact} (see FORM_FACTOR_COLLECTIONS), so the type
  // whitelist is loosened from ['compact'] to the whole refrigerator family —
  // a small top-freezer / single-door / drawer fridge (form_factor=mini) must
  // not be hard-excluded just because its door-config type isn't 'compact'.
  // Non-fridge types (beverage-cooler, freezers, ice makers) stay excluded, and
  // the SLUG_MAX_CUFT≤10 cap + category=refrigerator scope still bound the set.
  'mini-fridges': REFRIGERATOR_FAMILY_TYPES,
  'compact-refrigerators': REFRIGERATOR_FAMILY_TYPES,
  'compact-mini-fridges': REFRIGERATOR_FAMILY_TYPES,
  'dorm-refrigerators': REFRIGERATOR_FAMILY_TYPES,
  'dorm-fridges': REFRIGERATOR_FAMILY_TYPES, // FRIDGE-259 — see TYPE_TO_DB note
  // Chest freezers: only `chest-freezer` type — many products with this type
  // are mistyped (reach-in, work-top, merchandiser, hot cabinet) and need
  // title-based exclusion, but at least exclude wrong-type products.
  'chest-freezers': ['chest-freezer'],
  'deep-freezers': ['chest-freezer', 'deep-freezer'],
  // Kegerators: only `kegerator` type — compressors and tap kits mistyped
  // as kegerator stay in the type-allow but get caught by title patterns.
  'kegerators': ['kegerator'],
  // Ice makers: only `ice-maker` type.
  'ice-makers': ['ice-maker'],
  'built-in-ice-makers': ['ice-maker'],
  // Wine: wine-cooler and wine-fridge only.
  'wine-fridges': ['wine-cooler', 'wine-fridge'],
  'wine-coolers': ['wine-cooler', 'wine-fridge'],
  // Beverage: beverage-cooler only.
  'beverage-fridges': ['beverage-cooler'],
  'beverage-coolers': ['beverage-cooler'],
  'beverage-centers': ['beverage-cooler'],
}

export function getAllowedTypesForSlug(slug: string): readonly string[] | null {
  return SLUG_ALLOWED_TYPES[slug] ?? null
}

// =============================================================================
// CATEGORY SCOPING (Phase 1) — the audited `category` column is the single
// family source of truth, replacing the inconsistent is_refrigerator/is_freezer/
// is_specialty booleans (which mis-flagged e.g. top-freezer fridges as freezers,
// bleeding them into freezer collections/carousels).
//
// getCategoryScopeForSlug(slug) returns the category values a collection must be
// scoped to. Applied as an AND `category IN (...)` filter wherever products are
// served (collection page, collection API, carousel APIs). Derived from
// TYPE_TO_DB where possible; explicit overrides for title-only / boolean /
// tag-based / form-factor collections that have no clean type derivation.
// =============================================================================
type Category = 'refrigerator' | 'freezer' | 'specialty_cooling' | 'cooling_part'

const DB_TYPE_TO_CATEGORY: Record<string, Category> = {
  // refrigerator
  refrigerator: 'refrigerator', 'french-door': 'refrigerator', 'side-by-side': 'refrigerator',
  'top-freezer': 'refrigerator', 'bottom-freezer': 'refrigerator', 'counter-depth': 'refrigerator',
  compact: 'refrigerator', 'built-in': 'refrigerator', '4-door-flex': 'refrigerator',
  'single-door': 'refrigerator', drawer: 'refrigerator', freezerless: 'refrigerator',
  'mini-fridge': 'refrigerator',
  // freezer
  freezer: 'freezer', 'upright-freezer': 'freezer', 'chest-freezer': 'freezer',
  'compact-freezer': 'freezer', 'deep-freezer': 'freezer', 'drawer-freezer': 'freezer',
  // specialty cooling
  'wine-cooler': 'specialty_cooling', 'wine-fridge': 'specialty_cooling',
  'beverage-cooler': 'specialty_cooling', kegerator: 'specialty_cooling',
  'ice-maker': 'specialty_cooling', 'water-dispenser': 'specialty_cooling',
  'water-cooler': 'specialty_cooling', cooler: 'specialty_cooling',
  // cooling parts
  'water-filter': 'cooling_part', 'air-filter': 'cooling_part', 'condenser-filter': 'cooling_part',
  'ice-machine-filter': 'cooling_part', 'inline-filter': 'cooling_part',
  // FRIDGE-438: the true-part family (mirrors the classifier vocabulary and
  // lib/parts/fpn.ts TRUE_PART_TYPES) — non-appliance, so these can never
  // enter an appliance collection's category scope.
  'gasket': 'cooling_part', 'sensor': 'cooling_part', 'part': 'cooling_part',
}

const SLUG_CATEGORY_OVERRIDE: Record<string, Category[]> = {
  // Energy-efficient variants (title-only, the reported freezer bleed source)
  'energy-efficient-freezers': ['freezer'],
  'energy-efficient-refrigerators': ['refrigerator'],
  'energy-star-freezers': ['freezer'],
  'energy-star-refrigerators': ['refrigerator'],
  // Title-only refrigerator collections (no TYPE_TO_DB entry)
  'office-refrigerators': ['refrigerator'], 'apartment-refrigerators': ['refrigerator'],
  'garage-refrigerators': ['refrigerator'], 'garage-ready-refrigerators': ['refrigerator'],
  'quiet-refrigerators': ['refrigerator'], 'retro-fridges': ['refrigerator'],
  'portable-refrigerators': ['refrigerator'], 'kimchi-refrigerators': ['refrigerator'],
  'smart-fridges': ['refrigerator'], 'rv-refrigerators': ['refrigerator'],
  // Form-factor + title-only freezer collections
  'built-in-refrigerators': ['refrigerator'], 'counter-depth-refrigerators': ['refrigerator'],
  'undercounter-refrigerators': ['refrigerator'],
  'built-in-freezers': ['freezer'], 'undercounter-freezers': ['freezer'],
  'mini-freezers': ['freezer'], 'double-drawer-freezers': ['freezer'],
  'double-drawer-refrigerators': ['refrigerator'],
  'commercial-undercounter-freezers': ['freezer'],
  'humidity-control-refrigerators': ['refrigerator'],
  'portable-freezers': ['freezer'],
  // gaming pages are TITLE_SEARCH-only since FRIDGE-619; scope preserves the
  // original design's families (compact fridges + beverage coolers).
  'gaming-fridges': ['refrigerator', 'specialty_cooling'],
  'gaming-mini-fridges': ['refrigerator'],
  'freezer-coolers': ['freezer'], 'rv-freezers': ['freezer'],
}

export function getCategoryScopeForSlug(slug: string): Category[] | null {
  if (SLUG_CATEGORY_OVERRIDE[slug]) return SLUG_CATEGORY_OVERRIDE[slug]
  const types = TYPE_TO_DB[slug]
  if (types && types.length > 0) {
    const cats = Array.from(
      new Set(types.map((t) => DB_TYPE_TO_CATEGORY[t]).filter((c): c is Category => Boolean(c))),
    )
    return cats.length > 0 ? cats : null
  }
  return null
}

// =============================================================================
// FORM_FACTOR_COLLECTIONS: collections defined by the install/depth dimension
// (built-in, counter-depth, undercounter) rather than door-config `type`.
//
// Post-D2: products now carry a populated `form_factor text[]` column (the
// install/depth axis that used to be jammed into `type`). These collections
// match `form_factor @> {value}`, scoped to a product family, so they capture
// EVERY built-in/counter-depth/undercounter unit regardless of its door-config
// type (e.g. a counter-depth French-Door shows in both french-door AND
// counter-depth). Legacy `type='counter-depth'/'built-in'` rows still match too
// (back-compat), so this is purely additive — nothing that matched before stops.
// =============================================================================
// `family` is the `category` value the collection is scoped to (was the legacy
// is_refrigerator/is_freezer boolean; migrated to the audited `category` column).
export const FORM_FACTOR_COLLECTIONS: Record<
  string,
  // `family: null` scopes no category (FRIDGE-1646: commercial undercounter units
  // span refrigerators, freezers and specialty cooling).
  { formFactors: string[]; family: 'refrigerator' | 'freezer' | null }
> = {
  'built-in-refrigerators': { formFactors: ['built-in'], family: 'refrigerator' },
  'counter-depth-refrigerators': { formFactors: ['counter-depth'], family: 'refrigerator' },
  'undercounter-refrigerators': { formFactors: ['undercounter'], family: 'refrigerator' },
  'built-in-freezers': { formFactors: ['built-in'], family: 'freezer' },
  'undercounter-freezers': { formFactors: ['undercounter'], family: 'freezer' },
  // FRIDGE-1646: any commercial unit that installs under a counter; the
  // commercial flag is required by COMMERCIAL_ONLY_COLLECTIONS below.
  'commercial-undercounter': { formFactors: ['undercounter'], family: null },
  // Size collections (mini / compact). Unlike the depth collections above these
  // also carry a TYPE_TO_DB=['compact'] legacy entry + a SLUG_MAX_CUFT cap; the
  // form_factor match makes the SIZE axis authoritative so a small fridge of any
  // door-config (top-freezer, single-door, …) surfaces here, not only legacy
  // type='compact' rows. Paired with the loosened SLUG_ALLOWED_TYPES below
  // (compact → REFRIGERATOR_FAMILY_TYPES) so non-'compact' small fridges aren't
  // hard-excluded. Required before the Phase-4 type re-map (else re-typing a
  // compact→top-freezer would drop it from mini-fridges).
  // Scoped to the 'mini' size class only (classifier mini = ≤5 cu ft), matching
  // the retail/DOE "mini fridge" tier. 'compact' (5–7.5 cu ft) is deliberately
  // EXCLUDED — a 7 cu ft 2-door top-freezer is "apartment-size", not a mini
  // fridge. Legacy type='compact' rows still match via TYPE_TO_DB (bounded by
  // the SLUG_MAX_CUFT≤10 cap), so this is purely additive for true minis.
  'mini-fridges': { formFactors: ['mini'], family: 'refrigerator' },
  'compact-refrigerators': { formFactors: ['mini'], family: 'refrigerator' },
  'compact-mini-fridges': { formFactors: ['mini'], family: 'refrigerator' },
  'dorm-refrigerators': { formFactors: ['mini'], family: 'refrigerator' },
  'dorm-fridges': { formFactors: ['mini'], family: 'refrigerator' }, // FRIDGE-259
}

export function getFormFactorCollection(
  slug: string,
): { formFactors: string[]; family: 'refrigerator' | 'freezer' | null } | null {
  return FORM_FACTOR_COLLECTIONS[slug] ?? null
}

/**
 * Collections whose members must carry the catalog's own commercial flag
 * (products.is_commercial = true) on top of their type / title / form-factor
 * match. FRIDGE-1646: /collections/commercial-undercounter was registered with no
 * rule at all and matched every live product (6,939 of 7,551 uncapped, 10/17/2026);
 * its rule is an undercounter form factor or undercounter words in the title, and
 * this flag, applied by the page query, the count module, the collections API
 * route and its in-memory mirror alike.
 */
export const COMMERCIAL_ONLY_COLLECTIONS: ReadonlySet<string> = new Set(['commercial-undercounter'])

export function isCommercialOnlyCollection(slug: string | null | undefined): boolean {
  return !!slug && COMMERCIAL_ONLY_COLLECTIONS.has(slug)
}

// =============================================================================
// Collection-specific title exclusion patterns. The DB type column doesn't
// catch products that are obviously off-niche by title — e.g., the Whirlpool
// "15 in. Icemaker" has type='refrigerator' and the Cres Cor "Hot Cabinet"
// has type='chest-freezer'. These run AFTER the query at render time.
// =============================================================================
type SlugExcludes = { matches: (slug: string) => boolean; patterns: RegExp[] }

export const COLLECTION_TITLE_EXCLUDES: SlugExcludes[] = [
  // === Refrigerator family — block non-fridge appliances mistyped or
  //     title-leaking via "compact"/"mini" adjectives. Explicitly excludes
  //     wine-fridges, beer-fridges, beverage-fridges and other specialty
  //     "-fridges" slugs — those are wine/beverage collections where blocking
  //     "wine cellar"/"wine cooler" would wipe out the entire collection.
  {
    matches: (s) => {
      const SPECIALTY_FRIDGES = new Set([
        'wine-fridges',
        'wine-coolers',
        'beer-fridges',
        'beverage-fridges',
        'beverage-coolers',
        'beverage-centers',
        'beauty-fridges',
        'skincare-fridges',
        'makeup-fridges',
        'cosmetic-fridges',
        'kegerators',
        'ice-makers',
        'built-in-ice-makers',
      ])
      // A brand/color prefix does not turn a wine or beverage collection into
      // an ordinary refrigerator collection. The full slug is checked as well
      // as its parsed type by both the page and the count pipeline.
      if ([...SPECIALTY_FRIDGES].some(specialty => s === specialty || s.endsWith(`-${specialty}`))) return false
      return (
        s === 'refrigerators' ||
        s === 'mini-fridges' ||
        s === 'compact-refrigerators' ||
        s === 'compact-mini-fridges' ||
        s === 'dorm-refrigerators' ||
        s === 'dorm-fridges' ||
        s === 'apartment-refrigerators' ||
        s === 'apartment-size-refrigerators' ||
        s === 'portable-refrigerators' ||
        s.endsWith('-refrigerators') ||
        s.endsWith('-fridges')
      )
    },
    patterns: [
      /\bicemakers?\b/i,
      /\bice[\s-]+(maker|machine|cube[\s-]*maker)s?\b/i,
      /\bwine[\s-]+(cellar|cooler|fridge|chiller|cabinet)s?\b/i,
      /\bbeverage[\s-]+(cooler|center|fridge|station)s?\b/i,
      /\bkegerators?\b/i,
      // Standalone freezers (mini freezer, chest freezer, compact freezer,
      // deep freezer, garage freezer, upright freezer). Refrigerators-with-
      // freezer-compartment ("refrigerator with freezer drawer") are fine
      // because none of those phrases match these patterns.
      /\b(mini|compact|chest|upright|deep|garage|portable|small|countertop)[\s-]+freezers?\b/i,
      /\bfreezer[\s-]+(storage|organizer|basket|bag)\b/i,
      // Generic accessories. Narrowed phrasings to avoid blocking legit
      // refrigerators that mention door organizers as a feature.
      /\b(refrigerator|fridge)\s+(organizer|magnet|thermometer|deodorizer)\b/i,
      /\b(storage|drawer)\s+organizer\b/i,
      // OEM parts / control units mistyped as appliances.
      /\boem\s+part\b/i,
      /\bcontrol\s+unit\b/i,
      /\bgenuine\s+oem\b/i,
      // Commercial merchandisers / reach-ins / glass-door display fridges
      // mistyped as `type='compact'`.
      // One optional word allowed between "commercial" and the noun —
      // "Commercial Freestanding Refrigerator" dodged the strict adjacency
      // (FRIDGE-579/580, seen live on the dorm page 08/27/2026). Never a bare
      // /commercial/: "Commercial Cool" is a BRAND (the substring-on-identity
      // lesson), and its freestanding minis are genuine compacts.
      /\bcommercial[\s-]+(?:\w+[\s-]+)?(refrigerator|freezer|merchandiser|beverage[\s-]+refrigerator|reach[\s-]?in|cooler|combo|display)\b/i,
      /\bcommercial[\s-]+\d+/i,
      /\bglass[\s-]+door[\s-]+merchandiser\b/i,
    ],
  },

  // === Compact/mini/dorm-scoped ONLY — multi-door full-size mistyped as
  //     compact. Real compact fridges are single- or double-door — 3-door
  //     French Door, 4-door, and multi-door bespoke units are full-size
  //     regardless of `type='compact'` in the DB. These patterns previously
  //     sat in the broad `-refrigerators`/`-fridges` group above, which made
  //     every 4-door product invisible on EVERY refrigerator collection page
  //     (including /collections/4-door-refrigerators itself, which rendered
  //     "Shop 0" while 59 live products carry 4-door titles). Scope now
  //     matches the stated intent: size-constrained slugs only.
  {
    matches: (s) =>
      s === 'mini-fridges' ||
      s === 'compact-refrigerators' ||
      s === 'compact-mini-fridges' ||
      s === 'dorm-refrigerators' ||
      s === 'dorm-fridges' ||
      s === 'apartment-refrigerators' ||
      s === 'apartment-size-refrigerators' ||
      s === 'portable-refrigerators',
    patterns: [
      /\b4[\s-]?door\b/i,
      /\b(3|three)[\s-]?door[\s-]+french[\s-]+door/i,
      /\btriple[\s-]?door[\s-]+refrigerator\b/i,
      /\bquad[\s-]?door\b/i,
      /\bfamily\s+hub/i,
      // FRIDGE-579/580 — built-in DRAWER units ("24\" Wide 2-Drawer
      // All-Refrigerator", $1,867) rendered as the first card on the dorm
      // page. A drawer fridge is an installed built-in, not a mini fridge.
      // Digit-qualified or noun-adjacent only — "adjustable door drawer" as a
      // feature phrase must not match, and none of these forms appear in
      // genuine freestanding compact titles (counted against the live set).
      /\b\d[\s-]?drawer\b/i,
      /\bdrawer[\s-]+(refrigerator|fridge|all[\s-]?refrigerator)s?\b/i,
    ],
  },

  // === Dorm-scoped ONLY (FRIDGE-579/580, owner 08/27/2026). A dorm page
  //     must show what a student can actually put in a dorm room: no
  //     installed built-in/undercounter units, and no "Apartment Size"
  //     two-door 7+ cu ft fridges whose own titles say what they are for.
  //     Deliberately NOT applied to mini-fridges/compact-refrigerators —
  //     built-in compacts are genuinely mini fridges and keep those pages.
  {
    matches: (s) => s === 'dorm-refrigerators' || s === 'dorm-fridges',
    patterns: [
      /\b(built[\s-]?in|under[\s-]?counter|undercounter)\b/i,
      /\bapartment[\s-]?size\b/i,
    ],
  },

  // === Chest-freezer specific — block non-chest freezer formats mistyped
  //     as `chest-freezer` (worktop, reach-in, merchandiser, hot cabinet,
  //     fridge with freezer drawer, organizer accessories).
  //     Note: "basket" alone is NOT blocked because many chest freezers
  //     ship with removable baskets as a feature. Only block specific
  //     organizer/storage accessory phrasings.
  {
    matches: (s) => s === 'chest-freezers' || s === 'deep-freezers',
    patterns: [
      /\b(work[\s-]*top|reach[\s-]*in|merchandiser|display[\s-]*case|hot[\s-]*cabinet|pass[\s-]*thru|undercounter)\b/i,
      /\b(upright|stand[\s-]*up|reach)[\s-]+freezers?\b/i,
      /\bfreezer\s+(storage\s+)?(organizer|basket)\b/i,
      /\bstorage\s+(organizer|basket)\b/i,
      /\bfrench[\s-]+door\b/i,
      /\bwine[\s-]+(cellar|cooler|fridge|chiller)\b/i,
      /\bice[\s-]+(maker|machine)\b/i,
      /\bicemakers?\b/i,
      // Blast chillers / shock freezers are -40°F flash freezers for
      // restaurants/bakeries, not consumer chest freezers.
      /\bblast[\s-]+(chiller|freezer)\b/i,
      /\bshock[\s-]+(freezer|freezing|chiller)\b/i,
      /\bflash[\s-]+freezer\b/i,
      // Passive (non-powered) dry-ice storage containers and accessories.
      /\bdry[\s-]+ice[\s-]+(storage|chest|cover)\b/i,
      // Car / RV portable coolers and propane / gas-absorption fridges
      // mistyped as chest-freezer.
      /\bcar[\s-]+(refrigerator|cooler|fridge)\b/i,
      /\b(propane|gas[\s-]+absorption|gas)[\s-]+(fridge|refrigerator|cooler)\b/i,
      /\b\d{1,3}[\s-]?(quart|qt)\b/i,
      // Fridge accessory items mistyped as chest-freezer.
      /\bmini[\s-]+fridge[\s-]+(stand|with[\s-]+heating)/i,
      /\bfridge[\s-]+(stand|base)\b/i,
    ],
  },

  // === General freezer collections — block accessories and obvious
  //     mistypes. Note: do NOT block products whose title contains
  //     "refrigerator" because many commercial freezers have brand/company
  //     names like "Continental Refrigerator". The type whitelist already
  //     restricts to freezer types; only catch accessory items here.
  {
    matches: (s) =>
      s === 'freezers' ||
      s === 'mini-freezers' ||
      s === 'compact-freezers' ||
      s === 'small-chest-freezers' ||
      s === 'upright-freezers' ||
      s === 'apartment-freezers' ||
      s === 'portable-freezers',
    patterns: [
      /\bfreezer\s+(storage\s+)?organizer\b/i,
      /\bstorage\s+(organizer|basket)\b/i,
      /\bwine[\s-]+(cellar|cooler|fridge|chiller)\b/i,
      /\bice[\s-]+(maker|machine)\b/i,
      /\bicemakers?\b/i,
      /\bkegerators?\b/i,
    ],
  },

  // === Kegerators — block compressors, tap kits, draft towers, regulators,
  //     CO2 tanks, faucet handles, beer/gas line accessories, plus the large
  //     pile of mistyped products that have type='kegerator' in the DB:
  //     Summit wine dispensers, Kegco conversion kits, full-size French Door
  //     refrigerators, deli cases, lab equipment, beverage centers.
  {
    matches: (s) => s === 'kegerators',
    patterns: [
      /\bcompressors?\b/i,
      // Tap kits and draft tower accessories — but allow "Scratch-Resistant
      // Worktop" feature wording on real kegerators (no pattern blocks it).
      /\btap[\s-]+(kit|handle)\b/i,
      /\b(co2|co[\s-]*2)[\s-]+(tank|cylinder|regulator)\b/i,
      /\b(beer|gas)[\s-]+lines?\b/i,
      /\bcouplers?\b/i,
      /\bfaucet[\s-]+(handle|knob)\b/i,
      /\bkeg[\s-]+(coupler|connector)\b/i,
      /\bshank\b/i,
      /\bconversion[\s-]+kit\b/i,
      // Mistyped products on type='kegerator'.
      /\bwine[\s-]+dispenser\b/i,
      /\bwine[\s-]+(cellar|cabinet|chiller|cooler|fridge|refrigerator)\b/i,
      /\bbottle[\s-]+cooler\b/i,
      /\bback[\s-]+bar[\s-]+cooler\b/i,
      /\bdeli[\s-]+case\b/i,
      /\bbeverage[\s-]+center\b/i,
      /\bstackable[\s-]+base\b/i,
      /\bdisplay[\s-]+case\b/i,
      /\b(french|top|bottom|side[\s-]?by[\s-]?side)[\s-]?(door|freezer)[\s-]+refrigerator\b/i,
      /\bcompact[\s-]+single[\s-]?door[\s-]+refrigerator\b/i,
      /\bdual[\s-]+door[\s-]+(fridge|refrigerator)\b/i,
      /\bcompact[\s-]+refrigerator\b/i,
      /\b(reach[\s-]?in|serving[\s-]+cart|merchandiser|incubator)\b/i,
      /\bretro[\s-]+style[\s-]+top[\s-]+freezer/i,
    ],
  },

  // === Ice makers — block accessories/parts.
  {
    matches: (s) => s === 'ice-makers' || s === 'built-in-ice-makers',
    patterns: [
      /\b(replacement|cleaning)[\s-]+(part|kit|tablets?)\b/i,
      /\bice[\s-]+(scoop|bin|bucket|tray|mold|cube[\s-]+tray)\b/i,
      /\b(organizer|basket)\b/i,
    ],
  },

  // Wine coolers / beverage fridges intentionally NOT listed here — user
  // flagged them as mostly on-topic and warned against aggressive purging.
  // Title excludes for those should be added only with specific evidence.
]

export function getCollectionTitleExcludesForSlug(slug: string): RegExp[] {
  const result: RegExp[] = []
  for (const entry of COLLECTION_TITLE_EXCLUDES) {
    if (entry.matches(slug)) result.push(...entry.patterns)
  }
  return result
}

// =============================================================================
// Per-slug capacity caps. Compact / mini fridges have an inherent size
// limit — a 28-cu-ft product is not a "compact refrigerator" no matter what
// the DB type column says. We parse the largest cu.ft value mentioned in the
// title and reject products that exceed the cap.
// =============================================================================
export const SLUG_MAX_CUFT: Record<string, number> = {
  'mini-fridges': 10,
  'compact-refrigerators': 10,
  'compact-mini-fridges': 10,
  'dorm-refrigerators': 10,
}

export function getMaxCuftForSlug(slug: string): number | null {
  return SLUG_MAX_CUFT[slug] ?? null
}

const CUFT_RE = /(?<![\d.])(\d{1,3}(?:\.\d+)?)\s*(?:cu\.?\s*ft|cubic\s*(?:feet|foot|ft))/gi

export function extractMaxCuftFromTitle(title: string | null | undefined): number | null {
  if (!title) return null
  const values: number[] = []
  for (const m of title.matchAll(CUFT_RE)) {
    const v = parseFloat(m[1])
    if (!Number.isNaN(v)) values.push(v)
  }
  return values.length ? Math.max(...values) : null
}

// =============================================================================
// TITLE_SEARCH: Collections that ALSO search product titles for keywords
// Used for style-based collections like retro, smart, outdoor, etc.
// =============================================================================

export const TITLE_SEARCH: Record<string, string[]> = {
  // === SMART / CONNECTED ===
  'smart-fridges': ['smart', 'wifi', 'wi-fi', 'connected', 'family hub', 'instaview', 'thinq', 'hub'],
  
  // === STYLE-BASED ===
  'retro-refrigerators': ['retro', 'vintage', '50s', "50's", 'fifties', 'nostalgic', 'classic'],
  'retro-fridges': ['retro', 'vintage', '50s', "50's", 'fifties', 'nostalgic', 'classic'],
  'retro-freezers': ['retro', 'vintage', '50s', "50's", 'fifties', 'nostalgic', 'classic'],
  'door-in-door-refrigerators': ['door-in-door', 'door in door', 'instaview'],
  '4-door-refrigerators': ['4-door', '4 door', 'four door', 'four-door', 'quad door'],
  'fingerprint-resistant-refrigerators': ['fingerprint resistant', 'fingerprint-resistant', 'smudge proof', 'smudge-proof'],
  'black-stainless-refrigerators': ['black stainless', 'black-stainless'],
  'stainless-steel-refrigerators': ['stainless steel', 'stainless-steel'],
  
  // === GARAGE / OFFICE ===
  'garage-refrigerators': ['garage', 'garage ready', 'garage-ready', 'workshop', 'all climate'],
  'garage-ready-refrigerators': ['garage ready', 'garage-ready', 'extreme temperature', 'workshop'],
  'office-refrigerators': ['office', 'break room', 'workplace', 'small office'],
  'garage-ready-freezers': ['garage ready', 'garage-ready', 'extreme temperature', 'workshop'],
  
  // === LOCATION-BASED ===
  'outdoor-refrigerators': ['outdoor', 'patio', 'deck', 'weatherproof', 'weather proof', 'outdoor rated'],
  'outdoor-freezers': ['outdoor', 'weatherproof', 'weather proof', 'outdoor rated'],
  
  // === SIZE / SPACE-BASED ===
  // Bare adjectives are safe here because filter-typed products are excluded
  // at the query level (see FILTER_TYPES). The original keywords are restored
  // to keep coverage of legitimate fridges with these terms in their titles.
  'apartment-refrigerators': ['apartment', 'apartment size', 'apartment-size', 'apt size', 'narrow', 'slim', 'space saving', 'space-saving', 'small space', 'small kitchen'],
  'apartment-size-refrigerators': ['apartment', 'apartment size', 'apartment-size', 'apt size', 'narrow', 'slim', 'space saving', 'space-saving', 'small space', 'small kitchen'],
  'apartment-freezers': ['apartment', 'apartment size', 'apartment-size', 'apt size'],

  // === SMALL/COMPACT/MINI FRIDGES - includes skincare/makeup, NO french-door/side-by-side ===
  // Filter-typed accessory pollution is now blocked at the query level via
  // FILTER_TYPES exclusion, so bare adjectives are safe again.
  'mini-fridges': ['mini', 'small', 'compact', 'personal', 'portable', 'countertop', 'skincare', 'makeup', 'cosmetic', 'beauty'],
  'compact-refrigerators': ['mini', 'small', 'compact', 'personal', 'portable', 'countertop', 'skincare', 'makeup', 'cosmetic', 'beauty'],
  'compact-mini-fridges': ['mini', 'small', 'compact', 'personal', 'skincare', 'makeup'],
  'mini-freezers': ['mini', 'small', 'compact', 'personal', 'portable', 'countertop'],
  'compact-freezers': ['mini', 'small', 'compact', 'personal'],
  'small-chest-freezers': ['small', 'compact', 'mini', 'personal'],

  // === GAMING FRIDGES - desk-side / streaming setup / esports lounge intent ===
  // Title keywords match real gamer-targeted features (LED/RGB lighting,
  // desk/desktop placement) plus size cues that make a fridge fit on or
  // under a gaming desk. Same physical inventory as mini-fridges; the
  // gaming-fridges page exists for gamer search intent. Pages render only
  // when >=3 products pass the filter (Rule 3.5 enforced by render layer).
  // FRIDGE-619 — measured 08/29: the generic size terms ('mini', 'small',
  // 'compact', 'led', ...) matched 685/893 products — the whole mini-fridge
  // catalog; the gaming-marketed terms alone serve the 4-5 real units.
  'gaming-fridges': ['gaming', 'gamer', 'rgb'],
  'gaming-mini-fridges': ['gaming', 'gamer', 'rgb'],
  
  // === UNDERCOUNTER ===
  'undercounter-refrigerators': ['undercounter', 'under-counter', 'under counter', 'under-the-counter'],
  'undercounter-freezers': ['undercounter', 'under-counter', 'under counter', 'under-the-counter'],
  
  // === DRAWER ===
  // FRIDGE-619 — measured 08/29: bare 'drawer'/'pull-out' matched 68 NON-drawer
  // refrigerators ("french door with freezer drawer"); the tight phrase serves
  // the real family, including the Summit/Marvel/KitchenAid/Cafe "Refrigerator
  // Drawers" units mistyped as compact/freezerless/refrigerator (type-only =
  // 29 members, type OR phrase = 58, measured live).
  'drawer-refrigerators': ['refrigerator drawer'],
  'drawer-freezers': ['with drawer', 'drawer freezer', 'freezer drawer', 'pull-out freezer', 'pull out freezer'],
  'double-drawer-freezers': ['with drawers', 'freezer drawers', 'freezer drawer'],
  // FRIDGE-619 — refrigerator mirror of the double-drawer-freezers slug above;
  // category is confined to 'refrigerator' in SLUG_CATEGORY_OVERRIDE and the
  // page/count special cases, exactly like the freezer sibling. Terms measured
  // 08/29: 4 true double-drawer units — below the >=5 floor, so the row stays
  // gated until inventory grows, which is the honest state.
  'double-drawer-refrigerators': ['double drawer', 'dual drawer', 'two drawer'],
  
  // === FEATURE-BASED ===
  // FRIDGE-619 — measured 08/29: 'crisper'/'moisture control' matched 913
  // refrigerators (every crisper-drawer mention in the catalog); the humidity /
  // dual-evaporator terms alone serve the 12 real units.
  'humidity-control-refrigerators': ['humidity control', 'humidity-controlled', 'humidity controlled', 'dual evaporator', 'dual-evaporator'],
  'counter-depth-refrigerators': ['counter depth', 'counter-depth', 'counterdepth'],
  'freezerless-refrigerators': ['freezerless', 'all-refrigerator', 'all refrigerator', 'no freezer', 'refrigerator only'],
  'built-in-refrigerators': ['built-in', 'built in', 'panel ready', 'panel-ready', 'integrated', 'column', 'professional'],
  'built-in-freezers': ['built-in', 'built in', 'panel ready', 'panel-ready', 'integrated', 'column', 'professional'],
  // FRIDGE-619 — measured 08/29: the built-in/panel-ready/professional terms
  // (copied from the built-in-* rows above, where they belong) matched 398
  // refrigerators; 'column' alone serves the 7 refrigerator / 1 freezer true
  // column units.
  'column-refrigerators': ['column'],
  'column-freezers': ['column'],
  'portable-refrigerators': ['portable', '12v', '12 volt', 'car fridge', 'travel', 'camping', 'cooler'],
  // FRIDGE-619 — measured 08/29: 'mini'/'compact'/'personal' (plus the
  // TYPE_TO_DB compact-freezer mapping, removed the same day) made this a
  // 139-member duplicate of compact-freezers; the portability terms alone
  // serve the 10 real 12V/car/camping units.
  'portable-freezers': ['portable', '12v', '12 volt', 'car freezer', 'travel', 'camping'],
  'rv-refrigerators': ['rv refrigerator', 'rv fridge', ' rv ', 'for rv', 'camper fridge', 'motorhome', 'recreational vehicle'],
  'rv-freezers': ['rv freezer', 'rv freeze', ' rv ', 'for rv', 'camper freezer', 'motorhome freezer', 'recreational vehicle'],
  'kimchi-refrigerators': ['kimchi'],
  'freezer-coolers': ['freezer cooler', 'convertible', 'chest cooler', 'freezer-cooler'],
  
  // === SPECIALTY - Title search for keywords ===
  'quiet-refrigerators': ['quiet', 'silent', 'noise'],
  'quiet-freezers': ['quiet', 'silent', 'noise'],
  'beer-fridges': ['beer', 'brew', 'ale', 'lager'],
  'beverage-fridges': ['beverage', 'drink', 'soda', 'can', 'wine', 'beer', 'bottle', 'cooler'],
  'beverage-coolers': ['beverage', 'drink', 'soda', 'can', 'wine', 'beer', 'bottle', 'cooler'],
  'beverage-centers': ['beverage center', 'beverage-center', 'drink center'],
  'ice-makers': ['ice maker', 'ice machine', 'nugget ice', 'ice dispenser', 'icemaker'],
  'built-in-ice-makers': ['built-in ice', 'built in ice', 'undercounter ice', 'ice maker', 'ice machine'],
  'water-dispensers': ['water dispenser', 'water cooler', 'hot cold water', 'bottled water'],
  
  // === ENERGY EFFICIENT ===
  'energy-efficient-refrigerators': ['energy star', 'energy efficient', 'energy-star', 'energy-efficient', 'eco', 'high efficiency'],
  'energy-efficient-freezers': ['energy star', 'energy efficient', 'energy-star', 'energy-efficient', 'eco', 'high efficiency'],
  'energy-star-specialty-appliances': ['energy', 'efficient', 'eco', 'thermoelectric', 'inverter', 'compressor', 'quiet', 'low noise', 'dual zone', 'temperature'],
  
  // === WINE COOLER SUBTYPES (title search only — must contain wine-specific terms) ===
  'built-in-wine-coolers': ['built-in wine', 'built in wine', 'undercounter wine'],
  'dual-zone-wine-coolers': ['dual zone', 'dual-zone', 'two zone', 'two-zone', '2-zone', '2 zone'],
  'single-zone-wine-coolers': ['single zone', 'single-zone', 'one zone'],
  
  // === COMMERCIAL ===
  'commercial-refrigerators': ['commercial', 'reach-in', 'merchandiser', 'commercial refrigerator'],
  'commercial-freezers': ['commercial', 'reach-in freezer', 'commercial freezer'],
  'commercial-bar-coolers': ['bar cooler', 'back bar', 'underbar', 'bottle cooler'],
  'commercial-beverage-coolers': ['commercial beverage', 'commercial cooler', 'display refrigerator', 'display fridge', 'commercial glass door'],
  'commercial-kegerators': ['kegerator', 'keg cooler', 'beer dispenser', 'draft beer'],
  'back-bar-coolers': ['back bar', 'bar cooler', 'underbar', 'bottle cooler', 'beverage cooler'],
  'commercial-display-cases': ['display case', 'display', 'merchandiser', 'deli case', 'glass door'],
  'commercial-undercounter-refrigerators': ['commercial undercounter', 'worktop refrigerator', 'prep table refrigerator', 'commercial compact', 'commercial cool', 'commercial refrigerator'],
  'commercial-undercounter-freezers': ['commercial undercounter freezer', 'worktop freezer', 'prep table freezer'],
  // FRIDGE-1646: undercounter words; the commercial flag is required separately
  // (COMMERCIAL_ONLY_COLLECTIONS), so these never pull a home unit.
  'commercial-undercounter': ['undercounter', 'under counter', 'under-counter', 'worktop', 'prep table', 'back bar'],

  // === FILTERS ===
  'water-filters': ['water filter', 'refrigerator filter', 'fridge filter', 'replacement filter'],
  'refrigerator-water-filters': ['water filter', 'refrigerator filter', 'fridge filter'],
  'air-filters': ['air filter', 'deodorizer', 'freshflow', 'odor filter', 'fresh air filter'],
  'refrigerator-air-filters': ['air filter', 'deodorizer', 'freshflow', 'odor filter'],
  'condenser-filters': ['condenser filter', 'condenser screen', 'coil filter'],
  'ice-machine-filters': ['ice machine filter', 'ice maker filter', 'everpure', 'insurice'],
  'inline-filters': ['inline filter', 'in-line filter', 'water line filter'],
}

// =============================================================================
// WIDTH_RANGES: Width-based collections query the width_in column directly
// =============================================================================

export const WIDTH_RANGES: Record<string, { min: number; max: number }> = {
  // Narrow refrigerators (under 24 inches wide)
  'narrow-refrigerators': { min: 17, max: 24 },
  // Common refrigerator widths
  '18-inch-wide-refrigerators': { min: 17, max: 19 },
  '20-inch-wide-refrigerators': { min: 19, max: 21 },
  '21-inch-wide-refrigerators': { min: 20, max: 22 },
  '22-inch-wide-refrigerators': { min: 21, max: 23 },
  '23-inch-wide-refrigerators': { min: 22, max: 24 },
  '24-inch-wide-refrigerators': { min: 23, max: 25 },
  '26-inch-wide-refrigerators': { min: 25, max: 27 },
  '27-inch-wide-refrigerators': { min: 26, max: 28 },
  '28-inch-wide-refrigerators': { min: 27, max: 29 },
  '29-inch-wide-refrigerators': { min: 28, max: 30 },
  '30-inch-wide-refrigerators': { min: 29, max: 31 },
  '31-inch-wide-refrigerators': { min: 30, max: 32 },
  '32-inch-wide-refrigerators': { min: 31, max: 33 },
  '33-inch-wide-refrigerators': { min: 32, max: 34 },
  '34-inch-wide-refrigerators': { min: 33, max: 35 },
  '35-inch-wide-refrigerators': { min: 34, max: 36 },
  '36-inch-wide-refrigerators': { min: 35, max: 37 },
  '42-inch-wide-refrigerators': { min: 41, max: 43 },
  '48-inch-wide-refrigerators': { min: 47, max: 49 },
}

// =============================================================================
// CAPACITY_RANGES: Capacity-based collections (capacity_cuft or extract from title)
// =============================================================================

export const CAPACITY_RANGES: Record<string, { min: number; max: number }> = {
  'under-20-cu-ft-refrigerators': { min: 0, max: 20 },
  '20-to-24-cu-ft-refrigerators': { min: 20, max: 24 },
  '24-to-28-cu-ft-refrigerators': { min: 24, max: 28 },
  '28-cu-ft-and-up-refrigerators': { min: 28, max: 99 },
}

// =============================================================================
// BOTTLE_RANGES: Bottle-capacity hub collections (FRIDGE-44 filter-hub lane).
// Query the normalized products.bottle_capacity column (populated by the
// FRIDGE-33 spec-normalization pass) over the wine-cooler family. Bucket
// boundaries are IDENTICAL to the sidebar facet buckets (FACET_BOTTLE_BUCKETS
// in lib/collection-page-data.ts) so the hub URLs and the on-page facet
// describe the same segments. Buckets are half-open [min, max); the top bucket
// is open-ended (max: Infinity — consumers skip the upper bound when the max
// is not finite).
// =============================================================================

export const BOTTLE_RANGES: Record<string, { min: number; max: number }> = {
  'under-20-bottle-wine-coolers': { min: 1, max: 20 },
  '20-to-40-bottle-wine-coolers': { min: 20, max: 40 },
  '40-to-60-bottle-wine-coolers': { min: 40, max: 60 },
  '60-to-100-bottle-wine-coolers': { min: 60, max: 100 },
  '100-bottle-and-up-wine-coolers': { min: 100, max: Infinity },
}

/** The wine-cooler family the bottle hubs query over — matches the strict
 *  type whitelist the wine-cooler subtype collections already use
 *  (built-in-wine-coolers etc. in lib/collection-page-data.ts). */
export const BOTTLE_HUB_TYPES = ['wine-cooler', 'wine-fridge'] as const

// =============================================================================
// BOOLEAN_COLLECTIONS: Collections that use boolean flags for filtering
// =============================================================================

// Family collections (refrigerators/freezers/specialty) now scope by the audited
// `category` column instead of the inconsistent is_refrigerator/is_freezer/
// is_specialty booleans. `category` (set on every active row) is the source of
// truth; the `field` is retained only as a fallback for is_commercial (a separate
// axis with no category equivalent). Consumers prefer `category` when present.
export const BOOLEAN_COLLECTIONS: Record<
  string,
  { field: string; excludeCommercial: boolean; category?: ('refrigerator' | 'freezer' | 'specialty_cooling')[] }
> = {
  'refrigerators': { field: 'is_refrigerator', excludeCommercial: true, category: ['refrigerator'] },
  'freezers': { field: 'is_freezer', excludeCommercial: true, category: ['freezer'] },
  'specialty-appliances': { field: 'is_specialty', excludeCommercial: true, category: ['specialty_cooling'] },
  'specialty': { field: 'is_specialty', excludeCommercial: true, category: ['specialty_cooling'] },
  'commercial-refrigerators-and-freezers': { field: 'is_commercial', excludeCommercial: false },
  'commercial': { field: 'is_commercial', excludeCommercial: false },
}

// =============================================================================
// TYPE_GROUPS: Type synonyms - collections that show the same products
// =============================================================================

export const TYPE_GROUPS: Record<string, string[]> = {
  // Mini/Compact fridges group - all show same products
  'mini-fridges': ['mini-fridges', 'compact-refrigerators', 'compact-mini-fridges', 'dorm-refrigerators'],
  'compact-refrigerators': ['mini-fridges', 'compact-refrigerators', 'compact-mini-fridges', 'dorm-refrigerators'],
  'compact-mini-fridges': ['mini-fridges', 'compact-refrigerators', 'compact-mini-fridges', 'dorm-refrigerators'],
  'dorm-refrigerators': ['mini-fridges', 'compact-refrigerators', 'compact-mini-fridges', 'dorm-refrigerators'],

  // Gaming fridges group - desk-side / streaming setup intent
  'gaming-fridges': ['gaming-fridges', 'gaming-mini-fridges'],
  'gaming-mini-fridges': ['gaming-fridges', 'gaming-mini-fridges'],

  // Chest/Deep freezers group - all show same products
  'chest-freezers': ['chest-freezers', 'deep-freezers'],
  'deep-freezers': ['chest-freezers', 'deep-freezers'],
  'small-chest-freezers': ['small-chest-freezers'],

  // Compact/Mini freezers group
  'compact-freezers': ['compact-freezers', 'mini-freezers'],
  'mini-freezers': ['mini-freezers', 'compact-freezers'],

  // Wine storage group
  'wine-coolers': ['wine-coolers', 'wine-fridges'],
  'wine-fridges': ['wine-fridges', 'wine-coolers'],

  // Beverage group - includes wine, beer, drinks
  'beverage-coolers': ['beverage-coolers', 'beverage-fridges', 'beverage-centers'],
  'beverage-fridges': ['beverage-fridges', 'beverage-coolers', 'beverage-centers', 'beer-fridges', 'wine-fridges'],
  'beverage-centers': ['beverage-centers', 'beverage-coolers', 'beverage-fridges'],
  'beer-fridges': ['beer-fridges', 'beverage-coolers', 'beverage-fridges'],
}

// =============================================================================
// COLOR MAPPING
// =============================================================================

export const COLOR_TO_DB_PATTERNS: Record<string, string[]> = {
  'white': ['white'],
  'black': ['black'],
  'stainless-steel': ['stainless'],
  'black-stainless': ['black stainless', 'black-stainless'],
  'slate': ['slate'],
  'bisque': ['bisque'],
  'red': ['red'],
  'blue': ['blue'],
  'green': ['green'],
  'silver': ['silver'],
  'gray': ['gray', 'grey'],
  'grey': ['gray', 'grey'],
  'panel-ready': ['panel ready', 'panel-ready', 'custom panel'],
  'cream': ['cream', 'ivory'],
  'ivory': ['cream', 'ivory'],
}

/**
 * Does a product belong on a color collection? One rule for the served page
 * (lib/collection-membership.ts), the count script (lib/collection-page-count.ts)
 * and anything else that asks — owner 09/03/2026, after a visual audit of all
 * 16 color collections found black and stainless units on white pages:
 *  1. the BRAND is stripped from the title before matching — "Black+Decker"
 *     white freezers were on /collections/black-freezers (99 of 296 misfits);
 *  2. "stainless" never matches a black-stainless finish — the two are
 *     different collections;
 *  3. an explicit color column is the only thing consulted when present; the
 *     title is the fallback for rows with no color (an explicit finish beats
 *     a title keyword, so one column edit places a product).
 */
export function colorMatchesCollection(
  colorSlug: string,
  color: string | null | undefined,
  title: string | null | undefined,
  brand: string | null | undefined,
): boolean {
  const pats = COLOR_TO_DB_PATTERNS[colorSlug]
  const patterns = pats && pats.length > 0 ? pats : [colorSlug]
  const c = (color || '').toLowerCase()
  let t = (title || '').toLowerCase()
  const b = (brand || '').toLowerCase().trim()
  if (b) t = t.split(b).join(' ')
  // brand names that carry a color word, whatever the brand column says
  t = t.replace(/black\s*[+&]\s*decker|black and decker|blackdecker|black decker/g, ' ')
  // An explicit finish beats a title keyword: when the color column is set it
  // is the only thing consulted, so one column edit places a product. The
  // title is a fallback for rows with no color at all.
  // Panel-ready is a capability, not a finish: a "Panel Ready" title counts
  // whatever color the unit ships in.
  const hay = c.trim() && colorSlug !== 'panel-ready' ? c : `${c} | ${t}`
  // Creamsicle is an orange finish, not a cream token (FRIDGE-884).
  if (colorSlug === 'cream' || colorSlug === 'ivory') return /\b(?:cream|ivory)\b/.test(hay)
  if (colorSlug === 'stainless-steel' && /black[\s-]*stainless/.test(hay)) return false
  return patterns.some((x) => hay.includes(x.toLowerCase()))
}

// =============================================================================
// CANONICAL COLOR / FINISH VOCABULARY  (facet contamination root-cause fix)
// =============================================================================
// The raw products.color / products.finish columns are polluted with scraped
// variant strings: size values ("3.5 Cu.ft Black", "80 Cans", '24" Drawer
// Beverage Refrigerator'), dimensions ("173x132x38mm"), SKU/model fragments
// ("#211", "15nh-white"), and generic non-colors ("One Color", "Standard",
// "Glass Door", "Left Hinge"). A naive token split leaks ALL of these into the
// Color/Finish facet — which is why size values were showing up there.
//
// The clean derivation is an ALLOWLIST, not a blacklist (a blacklist can never
// enumerate every junk variant). We scan each raw string for known canonical
// color/finish terms and emit ONLY those: a real color buried inside a dirty
// string is recovered ("3.5 Cu.ft Black" -> Black), while any value with no
// recognised color term (pure size / SKU / dimension / generic junk) yields
// nothing and is dropped.
//
// Ordered MOST-SPECIFIC first, and each match is consumed, so a compound finish
// wins over the shorter terms nested inside it ("Black Stainless Steel" is one
// facet value, never Black + Stainless Steel).
const CANONICAL_COLORS: Array<[string, RegExp]> = [
  ['Black Stainless Steel', /\bblack[\s-]+stainless(?:[\s-]+steels?)?\b/],
  ['Matte Black', /\bmatte\s+black\b/],
  ['Champagne Bronze', /\bchampagne(?:\s+bronze)?\b/],
  ['Polished Gold', /\bpolished\s+gold\b/],
  ['Natural Wood', /\bnatural\s+wood\b/],
  ['Panel Ready', /\b(?:panel[\s-]*ready|custom\s+panel)\b/],
  ['Fingerprint Resistant', /\bfingerprint[\s-]*resistant\b/],
  ['Stainless Steel', /\bstainless(?:[\s-]+steels?)?\b/],
  ['Black', /\bblack\b/],
  ['White', /\bwhite\b/],
  ['Silver', /\b(?:silver|sliver)\b/], // "sliver" is a frequent scrape typo (23+ rows)
  ['Platinum', /\bplatinum\b/],
  ['Gray', /\b(?:gray|grey)\b/],
  ['Gold', /\bgold\b/],
  ['Bronze', /\bbronze\b/],
  ['Chrome', /\bchrome\b/],
  ['Red', /\bred\b/],
  ['Blue', /\bblue\b/],
  ['Green', /\bgreen\b/],
  ['Brown', /\bbrown\b/],
  ['Pink', /\bpink\b/],
  ['Purple', /\bpurple\b/],
  ['Yellow', /\byellow\b/],
  ['Beige', /\bbeige\b/],
  ['Cream', /\b(?:cream|ivory)\b/],
  ['Clear', /\bclear\b/],
]

/**
 * Extract the canonical color/finish terms present in one raw attribute value.
 * Returns [] when the string carries no recognised color — which is exactly how
 * size values, SKU codes, dimensions and generic junk are kept OUT of the
 * Color/Finish facet. Compound finishes match before their sub-words.
 */
export function canonicalizeColorValue(raw: string | null | undefined): string[] {
  if (!raw) return []
  let hay = ` ${String(raw).toLowerCase()} `
  const found: string[] = []
  for (const [name, re] of CANONICAL_COLORS) {
    if (re.test(hay)) {
      found.push(name)
      // Consume the matched region so nested shorter terms don't re-fire
      // (e.g. after "black stainless steel" matches, plain "black"/"stainless"
      // must not also count).
      hay = hay.replace(new RegExp(re.source, 'g'), ' ')
    }
  }
  return found
}

// =============================================================================
// BRAND NAME NORMALIZATION
// =============================================================================

const BRAND_DISPLAY_NAMES: Record<string, string> = {
  'lg': 'LG',
  'ge': 'GE',
  'ge-profile': 'GE Profile',
  'ge-cafe': 'Café',
  'samsung': 'Samsung',
  'whirlpool': 'Whirlpool',
  'frigidaire': 'Frigidaire',
  'kitchenaid': 'KitchenAid',
  'bosch': 'Bosch',
  'maytag': 'Maytag',
  'kenmore': 'Kenmore',
  'sub-zero': 'Sub-Zero',
  'thermador': 'Thermador',
  'viking': 'Viking',
  'jenn-air': 'Jenn-Air',
  'miele': 'Miele',
  'haier': 'Haier',
  'hisense': 'Hisense',
  'midea': 'Midea',
  'magic-chef': 'Magic Chef',
  'danby': 'Danby',
  'avanti': 'Avanti',
  'galanz': 'Galanz',
  'newair': 'NewAir',
  'insignia': 'Insignia',
  'zline': 'ZLINE',
  'true': 'TRUE',
}

// =============================================================================
// SPECIALTY TYPES (for filtering in specialty collections)
// =============================================================================

export const SPECIALTY_TYPES = [
  'wine-cooler',
  'wine-fridge',
  'beverage-cooler',
  'kegerator',
  'ice-maker',
  'water-dispenser',
]

// =============================================================================
// INTERFACES
// =============================================================================

export interface CollectionFilters {
  type?: string | null
  brand?: string | null
  color?: string | null
  priceMaxCents?: number | null
  isEnergyStar?: boolean
  /** Filters by tag overlap when USE_CASE_TAG_MAP maps it to live for-* tags;
   *  a use case with no map entry applies NO product predicate (SEO/display
   *  only — the pre-FRIDGE-259 behavior, kept deliberately so tagless
   *  use-case families are not zeroed ahead of the item-3 owner decision). */
  useCase?: string | null
  /** Exterior width bound (products.width_in, refrigerator family, NULL width
   *  is OUT) — populated from WIDTH_RANGES via the parsed type slug. */
  widthRange?: { min: number; max: number } | null
  /** Capacity bound (products.capacity_cuft, falling back to a title parse;
   *  NULL both is OUT) — populated from CAPACITY_RANGES via the parsed type
   *  slug. Half-open [min, max). */
  capacityRange?: { min: number; max: number } | null
}

// =============================================================================
// USE-CASE FACET — the rule, written down once (FRIDGE-259 item 2)
// =============================================================================

/**
 * Parser use-case slug -> live product tag(s). The right-hand tags are the 18
 * `for-*` tags actually carried by active+pdp_eligible products (live-derived
 * 08/14/2026). Every entry is a tight semantic match; a use case absent from
 * this map applies no product filter. `for-beverages` is deliberately
 * unmapped — no use case means "beverages"; that inventory lands through the
 * beverage TYPE collections. Guarded by tests/collection-usecase-filter.test.ts.
 */
export const USE_CASE_TAG_MAP: Record<string, string[]> = {
  'bedroom': ['for-bedrooms'],
  'dorm': ['for-dorms'],
  'dorm-room': ['for-dorms'],
  'college': ['for-dorms'],
  'office': ['for-offices'],
  'home-office': ['for-offices'],
  'restaurant': ['for-restaurants'],
  'commercial-kitchen': ['for-commercial'],
  'patio': ['for-patios'],
  'outdoor': ['for-outdoors'],
  'large-family': ['for-large-families'],
  'big-family': ['for-big-families'],
  'apartment': ['for-apartments'],
  'small-apartment': ['for-apartments'],
  'small-kitchen': ['for-small-kitchens'],
  'garage-storage': ['for-garages'],
  'bar': ['for-bars'],
  'home-bar': ['for-bars'],
  'sports-bar': ['for-bars'],
  'camping': ['for-camping'],
  'rv': ['for-rvs'],
  'camper': ['for-rvs'],
  'boat': ['for-marine'],
  'yacht': ['for-marine'],
  'wine-cellar': ['for-wine-storage'],
  'wine-room': ['for-wine-storage'],
}

/** Tags that decide membership for a parsed use case, or null when the use
 *  case is SEO/display-only (no map entry). */
export function getUseCaseTags(useCaseSlug: string | null | undefined): string[] | null {
  if (!useCaseSlug) return null
  return USE_CASE_TAG_MAP[useCaseSlug] || null
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get width range for a collection slug (if width-based)
 */
export function getWidthRange(slug: string): { min: number; max: number } | null {
  return WIDTH_RANGES[slug] || null
}

/**
 * Get capacity range for a collection slug (if capacity-based)
 */
export function getCapacityRange(slug: string): { min: number; max: number } | null {
  return CAPACITY_RANGES[slug] || null
}

/**
 * Get bottle-capacity range for a collection slug (if bottle-hub-based)
 */
export function getBottleRange(slug: string): { min: number; max: number } | null {
  return BOTTLE_RANGES[slug] || null
}

/**
 * Get boolean collection config (if boolean-based like refrigerators, freezers)
 */
export function getBooleanCollection(slug: string): { field: string; excludeCommercial: boolean } | null {
  return BOOLEAN_COLLECTIONS[slug] || null
}

/**
 * Get title search terms for a collection slug
 */
export function getTitleSearchTerms(slug: string): string[] | null {
  return TITLE_SEARCH[slug] || null
}

/**
 * Get database types for a collection slug
 */
export function getDbTypesForSlug(slug: string): string[] {
  const group = TYPE_GROUPS[slug] || [slug]
  const dbTypes: string[] = []
  
  group.forEach(t => {
    const types = TYPE_TO_DB[t]
    if (types) {
      dbTypes.push(...types)
    }
  })
  
  // Also add direct mapping
  const direct = TYPE_TO_DB[slug]
  if (direct) {
    dbTypes.push(...direct)
  }
  
  return [...new Set(dbTypes)]
}

/**
 * Extract capacity from product title
 * Matches patterns like "18 Cu. Ft.", "21.4 Cu.Ft.", "5.5 CF"
 */
export function extractCapacityFromTitle(title: string): number | null {
  const match = title.match(/(\d+\.?\d*)\s*(cu\.?\s*ft\.?|cf)/i)
  if (match) {
    return parseFloat(match[1])
  }
  return null
}

// =============================================================================
// APPLY FILTERS FUNCTION
// =============================================================================

/**
 * Apply collection filters to a Supabase query
 * This is the SINGLE SOURCE OF TRUTH for how filters are applied
 */
export function applyCollectionFilters(
  query: any,
  filters: CollectionFilters
): any {
  let q = query

  // 1. Type filter - use TYPE_GROUPS for related types
  if (filters.type) {
    const relatedTypes = TYPE_GROUPS[filters.type] || [filters.type]
    const dbTypes: string[] = []
    
    // Collect all database types for related collection slugs
    relatedTypes.forEach(t => {
      const types = TYPE_TO_DB[t]
      if (types) {
        dbTypes.push(...types)
      }
    })
    
    // Also add direct mapping for the filter type itself
    const directTypes = TYPE_TO_DB[filters.type]
    if (directTypes) {
      dbTypes.push(...directTypes)
    }
    
    // Deduplicate
    const uniqueDbTypes = [...new Set(dbTypes)]
    
    if (uniqueDbTypes.length > 0) {
      // Match any of the related database types
      q = q.in('type', uniqueDbTypes)
    } else {
      // Fallback: use tags if no DB type mapping found
      q = q.contains('tags', [filters.type])
    }
  }

  // 2. Brand filter - case-insensitive match
  if (filters.brand) {
    const brandName = filters.brand.replace(/-/g, ' ')
    q = q.ilike('brand', `${brandName}%`)
  }

  // 3. Color filter - check both color column and tags
  if (filters.color) {
    const colorPatterns = COLOR_TO_DB_PATTERNS[filters.color] || [filters.color]
    
    const orConditions: string[] = []
    colorPatterns.forEach(pattern => {
      orConditions.push(`color.ilike.%${pattern}%`)
    })
    orConditions.push(`tags.cs.{${filters.color}}`)
    
    q = q.or(orConditions.join(','))
  }

  // 4. Price filter
  if (filters.priceMaxCents) {
    q = q.lt('lowest_price_cents', filters.priceMaxCents)
    q = q.gt('lowest_price_cents', 0)
  }

  // 5. Energy Star filter - check tags OR specs_json
  if (filters.isEnergyStar) {
    q = q.or(`tags.cs.{energy-star},specs_json->>'Energy Star'.ilike.Yes%,specs_json->>'ENERGY STAR'.ilike.Yes%`)
  }

  // 6. Use case (FRIDGE-259 item 2): mapped use cases filter by tag overlap;
  // unmapped ones apply no predicate (SEO/display only).
  if (filters.useCase) {
    const useCaseTags = getUseCaseTags(filters.useCase)
    if (useCaseTags) {
      q = q.overlaps('tags', useCaseTags)
    }
  }

  // 7. Width (refrigerator family; NULL width_in never matches a range).
  if (filters.widthRange) {
    q = q.eq('category', 'refrigerator')
    q = q.gte('width_in', filters.widthRange.min)
    q = q.lte('width_in', filters.widthRange.max)
  }

  // 8. Capacity — the column half of the rule. The serving paths also accept
  // a title-parsed capacity for NULL capacity_cuft rows (a post-fetch step a
  // query cannot express); callers needing that fallback post-filter with
  // extractCapacityFromTitle, as the route's capacity branch does.
  if (filters.capacityRange) {
    q = q.eq('category', 'refrigerator')
    q = q.gte('capacity_cuft', filters.capacityRange.min)
    q = q.lt('capacity_cuft', filters.capacityRange.max)
  }

  return q
}

// =============================================================================
// CONVERT PARSED URL TO FILTERS
// =============================================================================

export function parsedUrlToFilters(parsed: ParsedCollectionUrl): CollectionFilters {
  return {
    type: parsed.typeSlug,
    brand: parsed.brandSlug,
    color: parsed.colorSlug,
    priceMaxCents: parsed.priceMaxCents,
    isEnergyStar: parsed.isEnergyStar,
    useCase: parsed.useCaseSlug,
    // Width/capacity ride the parsed type slug (the range tables key on it),
    // so brand x width combos like lg-21-inch-wide-refrigerators carry their
    // bound here too (FRIDGE-259 item 2).
    widthRange: parsed.typeSlug ? WIDTH_RANGES[parsed.typeSlug] || null : null,
    capacityRange: parsed.typeSlug ? CAPACITY_RANGES[parsed.typeSlug] || null : null,
  }
}

// =============================================================================
// DISPLAY FORMATTING
// =============================================================================

function titleCase(str: string): string {
  return str
    .split(/[-\s]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function getBrandDisplayName(brandSlug: string): string {
  return BRAND_DISPLAY_NAMES[brandSlug.toLowerCase()] || titleCase(brandSlug)
}

export function formatDisplayTitle(parsed: ParsedCollectionUrl): string {
  const parts: string[] = []

  if (parsed.colorName) parts.push(parsed.colorName)
  if (parsed.brandName) parts.push(parsed.brandName)
  if (parsed.typeName) parts.push(parsed.typeName)
  if (parsed.priceDisplay) parts.push(parsed.priceDisplay)
  if (parsed.isEnergyStar && parsed.filterCount > 1) parts.push('Energy Star')

  let title = parts.join(' ') || 'Products'

  // Use case with proper "For" capitalization - CAPITAL F!
  if (parsed.useCaseName) {
    title = `${title} For ${parsed.useCaseName}`
  }

  return title
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export function getTypeGroup(typeSlug: string): string[] {
  return TYPE_GROUPS[typeSlug] || [typeSlug]
}

export function areTypesEquivalent(type1: string, type2: string): boolean {
  const group1 = TYPE_GROUPS[type1]
  const group2 = TYPE_GROUPS[type2]
  
  if (group1 && group1.includes(type2)) return true
  if (group2 && group2.includes(type1)) return true
  
  return type1 === type2
}

/**
 * Check if a collection slug is a width-based collection
 */
export function isWidthBasedCollection(slug: string): boolean {
  return slug in WIDTH_RANGES
}

/**
 * Check if a collection slug is a capacity-based collection
 */
export function isCapacityBasedCollection(slug: string): boolean {
  return slug in CAPACITY_RANGES
}

/**
 * Check if a collection slug is a boolean-based collection
 */
export function isBooleanBasedCollection(slug: string): boolean {
  return slug in BOOLEAN_COLLECTIONS
}
